import { CheckCircle2, CircleHelp, FlaskConical, Plus } from 'lucide-react';
import type { AutomationTrigger } from '../../api/types';
import { identityLine } from '../../lib/channelMeta';
import { AUTOMATION_PRESETS } from './automationPresets';
import { CampaignsArt, FirstBookingArt, QuietArt, TripArt } from './campaignArt';
import type { SettingsSection } from './campaignSettingsTools';
import { useComms } from './context';
import { openSettingsTab } from './settingsTab';
import { useCampaignSetup } from './useCampaignSetup';

const STARTERS: { trigger: AutomationTrigger; art: typeof TripArt; title: string; hint: string }[] = [
  {
    trigger: 'trip_completed',
    art: TripArt,
    title: 'Thank them after a trip',
    hint: 'A day after each finished ride — a thank-you, or a review request.',
  },
  {
    trigger: 'first_booking',
    art: FirstBookingArt,
    title: 'Welcome first-time bookers',
    hint: 'Once, when somebody books with you for the first time.',
  },
  {
    trigger: 'lapsed',
    art: QuietArt,
    title: 'Win back quiet customers',
    hint: 'When their last trip is 90 days old and nothing is booked.',
  },
];

interface SetupStep {
  key: string;
  title: string;
  done: boolean;
  detail: string;
  action: { label: string; onClick: () => void } | null;
}

/**
 * The Campaigns home for a tenant that has never written a campaign: what a
 * campaign is for, the two ways to start (for real, or a walkthrough sent only
 * to the person trying it), the three automations most operators want first,
 * and what is left to set up — ticked from the tenant's own state through
 * `useCampaignSetup`, the same reads the Guide ticks from.
 */
export function CampaignsWelcome({
  mayDraft,
  onNew,
  onTryTest,
  onGuide,
  onSettings,
  onStartAutomation,
}: {
  mayDraft: boolean;
  onNew: () => void;
  onTryTest: () => void;
  onGuide: () => void;
  /** Campaigns → Settings, on a section — and, for Sender health, on one mailbox. */
  onSettings: (section?: SettingsSection, inboxId?: number) => void;
  onStartAutomation: (trigger: AutomationTrigger) => void;
}) {
  const { source, capabilities } = useComms();
  const mayConnect = capabilities?.access.features.connectChannels ?? false;
  const setup = useCampaignSetup();
  const { senders, mailboxes, managedMailboxes, checkedMailbox, websiteReady, hosts, linkDomain } = setup;

  const firstHealth = managedMailboxes[0];
  const healthDetail = checkedMailbox
    ? 'SPF, DKIM and DMARC check out'
    : !firstHealth
      ? 'Whoever manages your mailboxes can read this on each one’s settings.'
      : !firstHealth.health
        ? 'Not checked yet — it runs within a day of connecting.'
        : firstHealth.health.level === 'unknown'
          ? 'Could not check yet — the records did not answer.'
          : 'Needs a look before a big send.';

  const steps: SetupStep[] = [
    {
      key: 'sender',
      title: 'Connect where it sends from',
      done: senders.length > 0,
      detail: senders.length
        ? `${senders.slice(0, 2).map(identityLine).join(', ')}${senders.length > 2 ? ` and ${senders.length - 2} more` : ''}`
        : mayConnect
          ? 'A mailbox for email, or a Twilio or RingCentral number for texts.'
          : 'An administrator connects a mailbox or number.',
      action:
        !senders.length && mayConnect
          ? { label: 'Connect one', onClick: () => openSettingsTab('/connect', source.embedded) }
          : null,
    },
    // Only asked of a tenant that mails: a texts-only operator has no mailbox to check.
    ...(mailboxes.length
      ? [
          {
            key: 'health',
            title: 'Check the mailbox before a big send',
            done: checkedMailbox,
            detail: healthDetail,
            action:
              !checkedMailbox && firstHealth
                ? { label: 'Open its check', onClick: () => onSettings('health', firstHealth.inbox.id) }
                : null,
          },
        ]
      : []),
    {
      key: 'website',
      title: 'Count visits on your website',
      done: websiteReady,
      detail: websiteReady
        ? `Visits are counted on ${hosts[0]?.host ?? 'your website'}.`
        : 'Add the analytics tag so a campaign’s visits and reservations are counted.',
      action: websiteReady ? null : { label: 'See how', onClick: onGuide },
    },
    {
      key: 'domain',
      title: 'Put links on your own domain',
      done: Boolean(linkDomain?.verified),
      detail: linkDomain
        ? linkDomain.verified
          ? `Links use ${linkDomain.host}.`
          : `${linkDomain.host} is waiting for its DNS record.`
        : 'Optional — carriers and mail filters trust go.yourcompany.com more than a shared address.',
      action: linkDomain?.verified
        ? null
        : { label: linkDomain ? 'Check it' : 'Set up a link domain', onClick: () => onSettings('links') },
    },
  ];
  const done = steps.filter((step) => step.done).length;

  return (
    <div className="chome-welcome">
      <div className="chome-welcome-main">
        <section className="chome-hero">
          <CampaignsArt className="chome-hero-art" />
          <div className="chome-hero-text">
            <h2>Send your first campaign</h2>
            <p>
              Email or text the people who book with you — chosen by what they have booked — and see who
              opened, clicked, replied and booked afterwards.
            </p>
            {mayDraft ? (
              <>
                <div className="chome-hero-actions">
                  <button type="button" className="btn btn-primary" onClick={onNew}>
                    <Plus size={14} />
                    Start a campaign
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={onTryTest}>
                    <FlaskConical size={14} />
                    Try one, sent only to me
                  </button>
                </div>
                <span className="chome-muted">The tryout walks through each step and sends the result to you alone.</span>
              </>
            ) : (
              <span className="chome-muted">
                Your role can read campaigns but not write them — an administrator can change that under
                Users &amp; access.
              </span>
            )}
          </div>
        </section>

        <section className="chome-starters" aria-label="Automations to start from">
          <h2>Or start from one that sends itself</h2>
          <div className="chome-starter-grid">
            {STARTERS.map(({ trigger, art: Art, title, hint }) => (
              <button
                key={trigger}
                type="button"
                className="chome-starter"
                disabled={!mayDraft}
                title={mayDraft ? `Opens a new automation: ${AUTOMATION_PRESETS[trigger].name}` : undefined}
                onClick={() => onStartAutomation(trigger)}
              >
                <Art className="chome-starter-art" />
                <b>{title}</b>
                <span>{hint}</span>
              </button>
            ))}
          </div>
        </section>
      </div>

      <aside className="chome-setup" aria-label="Getting set up">
        <div className="chome-setup-eyebrow">
          Getting set up · {done} of {steps.length}
        </div>
        <div className="chome-setup-bar" aria-hidden>
          <span style={{ width: `${Math.round((done / steps.length) * 100)}%` }} />
        </div>
        <ol className="chome-setup-steps">
          {steps.map((step, index) => (
            <li key={step.key} className={step.done ? 'is-done' : ''}>
              <span className="chome-setup-dot" aria-label={step.done ? 'Done' : `Step ${index + 1}`}>
                {step.done ? <CheckCircle2 size={14} /> : index + 1}
              </span>
              <div>
                <b>{step.title}</b>
                <div className="chome-muted">{step.detail}</div>
                {step.action && (
                  <button type="button" className="an-link" onClick={step.action.onClick}>
                    {step.action.label}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ol>
        <button type="button" className="chome-setup-help" onClick={onGuide}>
          <CircleHelp size={16} />
          How campaigns work
        </button>
      </aside>
    </div>
  );
}
